import { useEffect, useState } from 'react';
import { loadWorldManifest } from './loader';
import type { WorldManifest } from './schema';
import { useSceneStore } from '../store/useSceneStore';

/**
 * React hook that loads + validates the world manifest once and flips
 * `useSceneStore.manifestLoaded` to true on success.
 *
 * Plan 02 mounts this hook at the App root; R3F consumes the returned manifest
 * to build the placeholder scene graph inside a <Suspense> boundary.
 */
export function useManifest(): { manifest: WorldManifest | null; error: Error | null } {
  const [manifest, setManifest] = useState<WorldManifest | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const setManifestLoaded = useSceneStore((s) => s.setManifestLoaded);

  useEffect(() => {
    let cancelled = false;
    loadWorldManifest()
      .then((m) => {
        if (!cancelled) {
          setManifest(m);
          setManifestLoaded(true);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e : new Error(String(e)));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [setManifestLoaded]);

  return { manifest, error };
}
